import {
  ChargeEvent,
  Device,
  DeviceStatusInfo,
  ExceptionEvent,
  FaultData,
  ReverseGeocodeAddress,
  Zone,
} from "@fairfleet/geotab";
import _ from "lodash";
import { calculateDuration } from "@/components/grid/cells/AddressCell";
import { faultComparator } from "@/hooks/faultData/faultComparator";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { DeviceDateStatusData } from "@/hooks/statusData/useStatusDataByDeviceAndDate";
import { DeviceHistoryEntry } from "@/models/DeviceHistoryEntry";
import { DeviceStatusOverviewData } from "@/models/DeviceStatusOverviewData";
import { DisplayMaintenance } from "@/models/MaintenanceDisplayData";
import { MaintenanceWorkOrder } from "@/models/MaintenanceWorkOrder";
import { MaintenanceWorkOrderJob } from "@/models/MaintenanceWorkOrderJob";
import ParkingAddInData from "@/models/ParkingAddInData";
import { RemainingRangeAddInData } from "@/models/RemainingRangeAddInData";
import { VehicleStatus } from "@/models/VehicleStatusAddInData";
import Warning, { WarningType } from "@/models/Warning";
import { DiagnosticAddinData } from "@/pages/diagnostic-edit/models/DiagnosticAddinData";
import { resolveDiagnosticOverride } from "@/pages/diagnostic-edit/utils/resolveDiagnosticOverride";

export default function combineStatusData(
  deviceData: Device[],
  addresses: Record<string, ReverseGeocodeAddress>,
  zones: { [key: string]: Zone },
  faults: FaultData[],
  descriptions: DiagnosticAddinData[],
  diagnosticNames: { [key: string]: { name: string } },
  statusInfos: Record<string, DeviceStatusInfo>,
  pmRuleIds: string[],
  pm1RuleIds: string[],
  adRuleIds: string[],
  redRuleId: string,
  yellowRuleId: string,
  sbRuleId: string,
  chargeEvents: Record<string, ChargeEvent[]>,
  pmEvents: Record<string, ExceptionEvent[]>,
  pm1Events: Record<string, ExceptionEvent[]>,
  adEvents: Record<string, ExceptionEvent[]>,
  showChargeRateOnlyWhenCharging: boolean,
  vehicleStatusGroups: VehicleStatus[],
  maintenanceWorkOrderJobs: Record<string, MaintenanceWorkOrderJob[]>,
  maintenanceWorkOrders: Record<string, MaintenanceWorkOrder[]>,
  latestHistoryByDevice: Record<string, DeviceHistoryEntry>,
  parkingData: ParkingAddInData[],
  possibleChargeInterruptData?: DeviceDateStatusData,
  remainingRangeData?: RemainingRangeAddInData[],
  odometerAdjustmentByDevice?: Record<string, number>,
): DeviceStatusOverviewData[] {
  return (
    deviceData?.map(device => {
      const warnings: Warning[] = [];
      const deviceId = device.id;
      const address = addresses?.[deviceId] ?? null;

      const statusInfo = statusInfos[deviceId] ?? null;
      const deviceEvents = statusInfo?.exceptionEvents ?? [];
      const redWarningEvent = deviceEvents.find(
        e => e?.rule?.id === redRuleId,
        // &&
        // ((e?.state as unknown as string) === "ExceptionEventStateValidId" ||
        //   includeDismissedWarnings),
      );
      const yellowWarningEvent = deviceEvents.find(
        e => e?.rule?.id === yellowRuleId,
        // &&
        // ((e?.state as unknown as string) === "ExceptionEventStateValidId" ||
        //   includeDismissedWarnings),
      );
      const sbWarningEvent = deviceEvents.find(e => e?.rule?.id === sbRuleId);
      const pmWarningEvents = _.uniqBy(
        pmRuleIds?.flatMap(rule =>
          (pmEvents[rule] || []).filter(event => event.device.id === deviceId),
        ),
        obj => obj.rule.id,
      );
      const pm1WarningEvents = _.uniqBy(
        pm1RuleIds.flatMap(rule =>
          (pm1Events[rule] || []).filter(event => event.device.id === deviceId),
        ),
        obj => obj.rule.id,
      );
      const adWarningEvents = _.uniqBy(
        adRuleIds.flatMap(rule =>
          (adEvents[rule] || []).filter(event => event.device.id === deviceId),
        ),
        obj => obj.rule.id,
      );

      const deviceGroupIds = device.groups?.map(g => g.id ?? "").filter(Boolean) ?? [];
      const deviceFaults = faults.filter(fault => fault.device.id === deviceId);
      const highestPriorityFault = deviceFaults.reduce<FaultData | null>((acc, fault) => {
        if (!acc) return fault;
        return faultComparator(descriptions, deviceGroupIds)(fault, acc) < 0 ? fault : acc;
      }, null);

      const faultDetails = highestPriorityFault
        ? resolveDiagnosticOverride(
            descriptions,
            highestPriorityFault.diagnostic.id,
            deviceGroupIds,
          )
        : null;

      // const warningData = pmWarningTextRuleId ? pmAddinDataCache[pmWarningTextRuleId] : null;
      const deviceChargeEvents = chargeEvents?.[deviceId];
      const sensorData = statusInfo?.statusData ?? [];
      const isCharging =
        sensorData?.find(s => s.diagnostic.id === "DiagnosticElectricVehicleChargingStateId")
          ?.data ?? -1;
      const ignitionStatus =
        sensorData?.find(s => s.diagnostic.id === "DiagnosticIgnitionId")?.data ?? -1;
      const chargeRate =
        sensorData?.find(s => s.diagnostic.id === "DiagnosticElectricVehicleBatteryPowerId")
          ?.data ?? null;
      const odometer =
        sensorData?.find(s => s.diagnostic.id === "DiagnosticOdometerId")?.data ?? null;
      const odometerAdjustment = odometerAdjustmentByDevice?.[deviceId] ?? null;

      const isDriving = statusInfo?.isDriving ?? false;

      const parking = parkingData?.find(p => p.deviceId === deviceId);

      let hasChargingInterruption = false;
      if (possibleChargeInterruptData?.[deviceId] && !isDriving) {
        const { date, data } = possibleChargeInterruptData[deviceId];
        const notDrivingSinceMillis = calculateDuration(
          statusInfo?.currentStateDuration ?? "",
          statusInfo?.dateTime ?? null,
        );

        const lastChargeAbortStartDateMillis = new Date().getTime() - date?.getTime();

        // Check that the device is not driving since it last finished charging
        hasChargingInterruption =
          lastChargeAbortStartDateMillis < notDrivingSinceMillis && data < 80;
      }

      if (redWarningEvent) {
        warnings.push({ type: WarningType.TT_RED, event: redWarningEvent });
      }
      if (yellowWarningEvent) {
        warnings.push({ type: WarningType.TT_YELLOW, event: yellowWarningEvent });
      }
      const MS_24H = 24 * 60 * 60 * 1000;
      if (sbWarningEvent && Date.now() - new Date(sbWarningEvent.activeFrom).getTime() <= MS_24H) {
        warnings.push({ type: WarningType.SB, event: sbWarningEvent });
      }

      if (pmWarningEvents.length > 0) {
        for (const event of pmWarningEvents) {
          warnings.push({ type: WarningType.PM, event });
        }
      }

      if (pm1WarningEvents.length > 0) {
        for (const event of pm1WarningEvents) {
          warnings.push({ type: WarningType.PM1, event });
        }
      }

      if (adWarningEvents.length > 0) {
        for (const event of adWarningEvents) {
          warnings.push({ type: WarningType.AD, event });
        }
      }

      const maintenance: DisplayMaintenance = {
        workOrderJobs: maintenanceWorkOrderJobs[deviceId],
        workOrders: maintenanceWorkOrders[deviceId],
      };

      return {
        id: deviceId,
        device,
        licensePlate: getDeviceLicensePlate(device),
        statusData: statusInfo,
        address: Object.keys(addresses).length > 0 ? (address?.formattedAddress ?? "") : null,
        zones:
          Object.keys(addresses).length > 0
            ? (address?.zones
                ?.map(zone => zones[zone?.id])
                ?.filter(z => z?.displayed)
                ?.map(z => z?.name)
                .join(", ") ?? "")
            : null,
        allZones: address?.zones?.map(zone => zones[zone?.id]) ?? [],
        ignitionStatus,
        isCharging: hasChargingInterruption ? 3 : isCharging,
        isDriving,
        faultPriority: faultDetails?.priority ?? -1,
        faultDescription: faultDetails?.description ?? null,
        faultInstruction: faultDetails?.instruction ?? "",
        faultType: highestPriorityFault ? highestPriorityFault.diagnostic.diagnosticType : "",
        faultName: highestPriorityFault
          ? diagnosticNames[highestPriorityFault.diagnostic.id]?.name
          : "",
        faultDId: highestPriorityFault?.diagnostic.id ?? "",
        warnings,
        chargingInterrupt: hasChargingInterruption,
        // chargeRate: isCharging === 0 ? 0 : isCharging !== -1 ? chargeRate : -1,
        chargeRate: showChargeRateOnlyWhenCharging
          ? isCharging > 0
            ? chargeRate
            : null
          : (chargeRate ?? null),
        chargeEvents: deviceChargeEvents ?? [],
        vehicleStatus: vehicleStatusGroups?.find(g => g.deviceId === deviceId),
        maintenance,
        latestRemark: latestHistoryByDevice[deviceId]?.message,
        odometer: odometer,
        odometerAdjustment: odometerAdjustment,
        parking: parking,
        remainingRange: remainingRangeData?.find(r => r.deviceId === deviceId),
        driverId: statusInfo?.driver?.id ?? undefined,
      };
    }) ?? []
  );
}
